import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, ShieldAlert, Image as ImageIcon } from "lucide-react";
import socket from "../socket";

// Helper: normalize ID format
const normalizeId = (id) =>
  typeof id === "string" ? id : id?._id || id?.id || (id ? String(id) : "");

const ChatPageCustomer = () => {
  const { sender: customerId, receiver: workerId, jobId } = useParams();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [workerInfo, setWorkerInfo] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);
  const [bidDetails, setBidDetails] = useState(null);
  const chatBoxRef = useRef(null);

  // 🔥 NEW — fullscreen image viewer state
  const [fullscreenImage, setFullscreenImage] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE;
  const API_CHAT = `${API_BASE}/api/chat`;

  const roomId = [customerId, workerId].sort().join("-") + `-${jobId}`;

  useEffect(() => {
    if (!customerId || !workerId || !jobId) return;

    socket.emit("joinRoom", roomId);
    fetchChatHistory();
    fetchWorkerInfo();
    fetchJobDetails();
    fetchBidDetails();
    markMessagesAsSeen();

    const handleReceive = (msg) => {
      if (String(msg.jobId) === String(jobId)) {
        if (msg.senderId === customerId) return;
        setMessages((prev) => [...prev, msg]);
        scrollToBottom();
      }
    };

    const handleTyping = (data) => {
      if (data.roomId === roomId && data.senderId !== customerId) {
        setIsTyping(true);
        setTimeout(() => setIsTyping(false), 1500);
      }
    };

    socket.on("receiveMessage", handleReceive);
    socket.on("typing", handleTyping);

    return () => {
      socket.off("receiveMessage", handleReceive);
      socket.off("typing", handleTyping);
    };
  }, [customerId, workerId, jobId]);

  // Fetch chat history
  const fetchChatHistory = async () => {
    try {
      const res = await axios.get(`${API_CHAT}/${customerId}/${workerId}/${jobId}`);
      setMessages(res.data || []);
      scrollToBottom();
    } catch (err) {
      console.error("❌ Failed to fetch messages", err);
    }
  };

  const fetchWorkerInfo = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/worker/profile/${workerId}`);
      setWorkerInfo(res.data);
    } catch (err) {
      console.error("❌ Worker info fetch failed", err);
    }
  };

  const fetchJobDetails = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/customers/jobs/job/${jobId}`);
      setJobDetails(res.data);
    } catch (err) {
      console.error("❌ Job details fetch failed", err);
    }
  };

  const fetchBidDetails = async () => {
    try {
      const res = await axios.get(
        `${API_BASE}/api/customers/jobs/bid/${jobId}/${workerId}`
      );
      setBidDetails(res.data);
    } catch {
      console.warn("⚠️ No bid found");
    }
  };

  const markMessagesAsSeen = async () => {
    try {
      await axios.post(`${API_CHAT}/mark-seen`, { customerId, workerId, jobId });
    } catch (err) {
      console.error("❌ Failed to mark seen", err);
    }
  };

  const handleTyping = (e) => {
    setInput(e.target.value);
    socket.emit("typing", { roomId, senderId: customerId });
  };

  const sendMessage = async () => {
    if (!input.trim()) return;

    const payload = {
      senderId: customerId,
      receiverId: workerId,
      message: input,
      jobId,
      roomId,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, payload]);
    setInput("");
    scrollToBottom();

    try {
      socket.emit("sendMessage", payload);
      await axios.post(`${API_CHAT}/send`, payload);
    } catch (err) {
      console.error("❌ Send error:", err);
    }
  };

  // 🔥 IMAGE UPLOAD
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);
    formData.append("senderId", customerId);
    formData.append("receiverId", workerId);
    formData.append("jobId", jobId);
    formData.append("roomId", roomId);

    try {
      const res = await axios.post(`${API_CHAT}/upload-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const msg = res.data.message;
      setMessages((prev) => [...prev, msg]);
      scrollToBottom();
    } catch (err) {
      console.error("❌ Image upload failed", err);
    }
  };

  const scrollToBottom = () =>
    setTimeout(() => {
      chatBoxRef.current?.scrollTo({
        top: chatBoxRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 100);

  const formatTime = (time) =>
    time && !isNaN(new Date(time))
      ? new Date(time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : "";

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-green-50 to-gray-100 justify-center">
      <div className="flex flex-col w-full max-w-3xl p-6">

        {/* ===== Worker Header ===== */}
        {workerInfo && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-center mb-6 bg-white/70 backdrop-blur-md border border-green-100 rounded-2xl p-6 shadow-lg"
          >
            <Link to={`/worker-profile/${workerId}`}>
              <img
                src={
                  workerInfo.profilePhotoUrl
                    ? `${API_BASE}${workerInfo.profilePhotoUrl}`
                    : "/default-avatar.png"
                }
                alt="Worker"
                className="w-20 h-20 mx-auto rounded-full border-2 border-green-600 shadow hover:scale-105 transition-transform"
              />
            </Link>
            <h2 className="mt-3 text-xl font-semibold text-gray-800">
              {workerInfo.name}
            </h2>
            <p className="text-gray-500 text-sm">
              {jobDetails?.jobTitle || "Service"} •{" "}
              {bidDetails?.price ? `$${bidDetails.price}` : "Pending"}
            </p>

            <div className="mt-4 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2">
              <ShieldAlert size={16} />
              <span>
                All payments must go through BYN. Avoid direct cash transfers.
              </span>
            </div>
          </motion.div>
        )}

        {/* ===== Chat Box ===== */}
        <div
          ref={chatBoxRef}
          className="flex-1 bg-white rounded-2xl p-4 shadow-inner border overflow-y-auto"
        >
          {messages.length === 0 ? (
            <p className="text-gray-400 text-center mt-10">
              Start the conversation 👋
            </p>
          ) : (
            messages.map((msg, idx) => {
              const isMine = normalizeId(msg.senderId) === String(customerId);
              const messageTime = msg.timestamp || msg.createdAt;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`mb-2 flex ${isMine ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`px-4 py-2 rounded-2xl shadow-sm max-w-xs sm:max-w-sm ${
                      isMine
                        ? "bg-green-600 text-white rounded-br-none"
                        : "bg-gray-100 text-gray-900 rounded-bl-none"
                    }`}
                  >
                    {/* Text */}
                    {msg.message && <p>{msg.message}</p>}

                    {/* Image bubble — now clickable */}
                    {msg.imageUrl && (
                      <img
                        src={`${API_BASE}${msg.imageUrl}`}
                        alt="sent"
                        className="mt-2 rounded-lg max-w-[180px] cursor-pointer hover:opacity-80 transition"
                        onClick={() => setFullscreenImage(`${API_BASE}${msg.imageUrl}`)}
                      />
                    )}

                    <p className="text-xs text-right mt-1 opacity-70">
                      {formatTime(messageTime)}
                    </p>
                  </div>
                </motion.div>
              );
            })
          )}

          {isTyping && (
            <p className="text-xs text-gray-500 italic mt-2 animate-pulse">
              Worker is typing...
            </p>
          )}
        </div>

        {/* ===== Input Area ===== */}
        <div className="mt-4 flex items-center bg-white rounded-full border shadow px-4 py-2">

          <input
            type="file"
            accept="image/*"
            id="chatImage"
            hidden
            onChange={handleImageUpload}
          />

          <button
            onClick={() => document.getElementById("chatImage").click()}
            className="mr-3 text-gray-600 hover:text-gray-900"
          >
            <ImageIcon size={22} />
          </button>

          <input
            type="text"
            value={input}
            onChange={handleTyping}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            placeholder="Type your message..."
            className="flex-grow px-3 py-2 text-gray-800 bg-transparent focus:outline-none"
          />

          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={sendMessage}
            className="bg-green-600 hover:bg-green-700 text-white p-3 rounded-full shadow-md"
          >
            <Send size={18} />
          </motion.button>
        </div>
      </div>

      {/* 🔥🔥 FULLSCREEN IMAGE VIEWER 🔥🔥 */}
      {fullscreenImage && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
          onClick={() => setFullscreenImage(null)}
        >
          <img
            src={fullscreenImage}
            className="max-w-full max-h-full rounded-xl shadow-xl"
            alt="Full View"
          />
        </div>
      )}
    </div>
  );
};

export default ChatPageCustomer;
